import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, Grid, IconButton, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IconAlertTriangle, IconCheck, IconDownload } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import EntityPickerInline from '../ticket/EntityPickerInline';
import CustomFieldInput from '../authentication/configuracoes/shared/CustomFieldInput';
import { isFieldVisible } from '../authentication/configuracoes/shared/fieldVisibility';
import {
    CONTRATO_STATUS_COLORS,
    CONTRATO_STATUS_LABELS,
    STATUS_PAGAMENTO_COLORS,
    STATUS_PAGAMENTO_LABELS,
    formatServicoDate,
    recorrenciaResumo
} from './servicoUtils';

const chipSelectSx = (colors) => ({
    height: 28,
    fontWeight: 600,
    textTransform: 'none',
    bgcolor: colors?.bg,
    color: colors?.color,
    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0 }
});

// Detalhe de um Contrato de Serviço - mesmo padrão de patch-on-change de
// TicketDetailDrawer.js (`patchContrato`), reaproveitando EntityPickerInline
// pra trocar cliente/organização.
const ContratoDetailDrawer = ({ appId, contratoId, onClose, onChanged }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const history = useHistory();

    const [contrato, setContrato] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [marcandoPago, setMarcandoPago] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [observacoes, setObservacoes] = useState('');
    const [clienteMenuOpen, setClienteMenuOpen] = useState(false);
    const [orgMenuOpen, setOrgMenuOpen] = useState(false);

    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [customFieldValues, setCustomFieldValues] = useState({});
    const [savingFields, setSavingFields] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);

    useEffect(() => {
        if (!appId) return;
        api
            .get(`v1/apps/${appId}/contrato_servico_fields/`)
            .then((response) => setFieldsConfig(response.data || []))
            .catch((error) => console.error('Erro ao carregar campos customizados', error));
    }, [appId]);

    const load = async () => {
        if (!appId || !contratoId) return;
        setLoading(true);
        try {
            const response = await api.get(`v1/apps/${appId}/contratos_servico/${contratoId}/`);
            setContrato(response.data);
            setObservacoes(response.data.observacoes || '');
            const values = {};
            (response.data.field_values || []).forEach((fv) => {
                values[fv.field_name] = fv.value;
            });
            setCustomFieldValues(values);
        } catch (error) {
            console.error('Erro ao carregar contrato', error);
            setStatus({ type: 'error', message: 'Erro ao carregar Contrato.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, contratoId]);

    const notifyChanged = () => {
        if (onChanged) onChanged();
    };

    const patchContrato = async (payload) => {
        setSaving(true);
        try {
            const response = await api.patch(`v1/apps/${appId}/contratos_servico/${contratoId}/`, payload);
            setContrato(response.data);
            notifyChanged();
        } catch (error) {
            console.error('Erro ao atualizar contrato', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao atualizar Contrato.' });
        } finally {
            setSaving(false);
        }
    };

    const handleMarcarPago = async () => {
        setMarcandoPago(true);
        setStatus({ type: '', message: '' });
        try {
            const response = await api.post(`v1/apps/${appId}/contratos_servico/${contratoId}/marcar_pago/`);
            setContrato(response.data);
            notifyChanged();
        } catch (error) {
            console.error('Erro ao marcar como pago', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao marcar como pago.' });
        } finally {
            setMarcandoPago(false);
        }
    };

    const handleObservacoesBlur = () => {
        if (observacoes !== (contrato?.observacoes || '')) {
            patchContrato({ observacoes });
        }
    };

    const handleSaveCustomFields = async () => {
        setSavingFields(true);
        try {
            const response = await api.patch(`v1/apps/${appId}/contratos_servico/${contratoId}/`, { fields: customFieldValues });
            setContrato(response.data);
            notifyChanged();
        } catch (error) {
            console.error('Erro ao salvar campos customizados', error);
            setStatus({ type: 'error', message: 'Erro ao salvar campos customizados.' });
        } finally {
            setSavingFields(false);
        }
    };

    const handleExportPdf = async () => {
        setExportingPdf(true);
        try {
            const response = await api.get(`v1/apps/${appId}/contratos_servico/${contratoId}/gerar_pdf/`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `contrato_${contratoId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao exportar PDF', error);
            setStatus({ type: 'error', message: 'Erro ao exportar PDF.' });
        } finally {
            setExportingPdf(false);
        }
    };

    if (!contratoId) return null;

    return (
        <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 0 } }}>
            {loading || !contrato ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                {contrato.servico_detail?.nome}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                {recorrenciaResumo(contrato.servico_detail)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={exportingPdf ? <CircularProgress size={14} /> : <IconDownload size={16} />}
                                onClick={handleExportPdf}
                                disabled={exportingPdf}
                            >
                                Exportar PDF
                            </Button>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {status.message && (
                        <Alert severity={status.type} onClose={() => setStatus({ type: '', message: '' })}>
                            {status.message}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                        {contrato.is_overdue && (
                            <Chip
                                icon={<IconAlertTriangle size={15} />}
                                label="Vencido"
                                size="small"
                                sx={{ fontWeight: 700, bgcolor: '#FBD5D5', color: '#B42121' }}
                            />
                        )}

                        <TextField
                            select
                            size="small"
                            disabled={saving}
                            value={contrato.status}
                            onChange={(e) => patchContrato({ status: e.target.value })}
                            sx={{ minWidth: 150, '& .MuiOutlinedInput-root': chipSelectSx(CONTRATO_STATUS_COLORS[contrato.status]) }}
                        >
                            {Object.entries(CONTRATO_STATUS_LABELS).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Chip
                            label={STATUS_PAGAMENTO_LABELS[contrato.status_pagamento]}
                            size="small"
                            sx={{ fontWeight: 700, ...STATUS_PAGAMENTO_COLORS[contrato.status_pagamento] }}
                        />

                        {contrato.status_pagamento === 'pendente' && (
                            <Button size="small" variant="outlined" startIcon={<IconCheck size={15} />} onClick={handleMarcarPago} disabled={marcandoPago}>
                                {marcandoPago ? <CircularProgress size={16} /> : 'Marcar como pago'}
                            </Button>
                        )}
                    </Box>

                    <Divider />

                    <Box display="flex" gap={2}>
                        <TextField
                            label="Valor (R$)"
                            type="number"
                            fullWidth
                            size="small"
                            defaultValue={contrato.valor}
                            onBlur={(e) => {
                                if (e.target.value !== String(contrato.valor)) patchContrato({ valor: e.target.value });
                            }}
                        />
                        <TextField
                            label="Vencimento"
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={contrato.data_vencimento || ''}
                            onChange={(e) => patchContrato({ data_vencimento: e.target.value || null })}
                        />
                    </Box>

                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Início em {formatServicoDate(contrato.data_inicio)}
                        {contrato.data_ultimo_pagamento && ` · Último pagamento em ${formatServicoDate(contrato.data_ultimo_pagamento)}`}
                    </Typography>

                    <TextField
                        label="Observações"
                        fullWidth
                        multiline
                        rows={2}
                        value={observacoes}
                        onChange={(e) => setObservacoes(e.target.value)}
                        onBlur={handleObservacoesBlur}
                    />

                    {fieldsConfig.length > 0 && (
                        <>
                            <Divider />
                            <Typography fontWeight={600}>Campos personalizados</Typography>
                            <Grid container spacing={2}>
                                {fieldsConfig
                                    .filter((f) => isFieldVisible(f, fieldsConfig, customFieldValues))
                                    .map((f) => (
                                        <CustomFieldInput
                                            key={f.id}
                                            field={f}
                                            value={customFieldValues[f.name]}
                                            onChange={(value) => setCustomFieldValues({ ...customFieldValues, [f.name]: value })}
                                        />
                                    ))}
                            </Grid>
                            <Box>
                                <Button size="small" variant="outlined" onClick={handleSaveCustomFields} disabled={savingFields}>
                                    {savingFields ? <CircularProgress size={16} /> : 'Salvar campos personalizados'}
                                </Button>
                            </Box>
                        </>
                    )}

                    <Divider />

                    <Box sx={{ p: 1, borderRadius: 2, background: isDark ? '#1f2c33' : '#f7f8fa' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography fontWeight={600}>Cliente</Typography>
                            <Button size="small" onClick={() => setClienteMenuOpen((v) => !v)}>
                                Trocar
                            </Button>
                        </Box>
                        {contrato.cliente_detail ? (
                            <Typography variant="body2">
                                {contrato.cliente_detail.first_name} {contrato.cliente_detail.last_name} ({contrato.cliente_detail.email})
                            </Typography>
                        ) : (
                            <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                Nenhum cliente vinculado.
                            </Typography>
                        )}
                        {clienteMenuOpen && (
                            <Box sx={{ mt: 1 }}>
                                <EntityPickerInline
                                    label="Buscar cliente"
                                    placeholder="Buscar cliente por nome ou e-mail..."
                                    searchUrl={(term) => `v1/apps/${appId}/users/?search=${term}`}
                                    extractResults={(data) => data.users}
                                    getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`}
                                    value={null}
                                    onSelect={(u) => {
                                        setClienteMenuOpen(false);
                                        patchContrato({ cliente: u.id });
                                    }}
                                    onClear={() => {
                                        setClienteMenuOpen(false);
                                        patchContrato({ cliente: null });
                                    }}
                                    clearLabel="Sem cliente"
                                />
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ p: 1, borderRadius: 2, background: isDark ? '#1f2c33' : '#f7f8fa' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography fontWeight={600}>Organização</Typography>
                            <Button size="small" onClick={() => setOrgMenuOpen((v) => !v)}>
                                Trocar
                            </Button>
                        </Box>
                        {contrato.organization_detail ? (
                            <Typography variant="body2">{contrato.organization_detail.name}</Typography>
                        ) : (
                            <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                Nenhuma organização vinculada.
                            </Typography>
                        )}
                        {orgMenuOpen && (
                            <Box sx={{ mt: 1 }}>
                                <EntityPickerInline
                                    label="Buscar organização"
                                    placeholder="Buscar organização..."
                                    searchUrl={(term) => `v1/apps/${appId}/organizations/?search=${term}`}
                                    extractResults={(data) => data.organizations}
                                    getOptionLabel={(org) => org.name}
                                    value={null}
                                    onSelect={(org) => {
                                        setOrgMenuOpen(false);
                                        patchContrato({ organization: org.id });
                                    }}
                                    onClear={() => {
                                        setOrgMenuOpen(false);
                                        patchContrato({ organization: null });
                                    }}
                                    clearLabel="Sem organização"
                                />
                            </Box>
                        )}
                    </Box>

                    {contrato.deal_detail && (
                        <Box
                            sx={{ p: 1, borderRadius: 2, background: isDark ? '#1f2c33' : '#f7f8fa', cursor: 'pointer' }}
                            onClick={() => history.push('/dashboard/config/pipeline')}
                        >
                            <Typography fontWeight={600}>Negócio relacionado</Typography>
                            <Typography variant="body2">{contrato.deal_detail.title}</Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Drawer>
    );
};

export default ContratoDetailDrawer;
